import React, { useEffect, useState } from "react";
import { Mail, ShoppingBag, MessageSquare, Trash2, CheckCheck, User, Loader2, Send, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KIND = {
  compra: ["Compra", ShoppingBag, "bg-emerald-100 text-emerald-700"],
  contacto: ["Contacto", MessageSquare, "bg-sky-100 text-sky-700"],
  comentario: ["Comentario", MessageSquare, "bg-amber-100 text-amber-700"],
  whatsapp: ["WhatsApp", MessageSquare, "bg-green-100 text-green-700"],
  prueba: ["Prueba", Mail, "bg-gray-100 text-gray-700"],
  cliente: ["Al cliente", User, "bg-gray-100 text-gray-700"],
};

export default function InboxAdmin({ onChange }) {
  const [notes, setNotes] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [open, setOpen] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  useEffect(() => { api.get("/admin/email-status").then((r) => setEmailStatus(r.data)).catch(() => {}); }, []);
  const sendTest = async () => {
    setTesting(true);
    try { const { data } = await api.post("/admin/notifications/test"); toast.success(`Correo de prueba enviado a ${data.to}`); setTimeout(load, 4000); }
    catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  const load = () => {
    api.get("/admin/notifications").then((r) => setNotes(r.data));
    api.get("/admin/messages").then((r) => setMsgs(r.data));
  };
  useEffect(() => { load(); }, []);

  const readAll = async () => { await api.post("/admin/notifications/read-all"); load(); onChange(); };
  const readMsg = async (m) => { setOpen(open === m.id ? null : m.id); if (!m.read) { await api.patch(`/admin/messages/${m.id}/read`); load(); onChange(); } };
  const delMsg = async (m) => { await api.delete(`/admin/messages/${m.id}`); load(); onChange(); };

  if (!notes || !msgs) return <Loader2 className="w-6 h-6 animate-spin brand-text" />;

  return (
    <div data-testid="admin-inbox">
      {emailStatus && (emailStatus.configured ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm p-3 flex flex-wrap items-center justify-between gap-3" data-testid="email-status-ok">
          <span>Correos reales <b>ACTIVOS</b> · se envían desde <b>{emailStatus.from}</b>. Las facturas PDF van adjuntas.</span>
          <button onClick={sendTest} disabled={testing} className="btn-brand px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 disabled:opacity-60" data-testid="send-test-email-btn">
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Enviar correo de prueba
          </button>
        </div>
      ) : (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm p-3" data-testid="email-status-mock">
          Envío de correos <b>SIMULADO</b>: falta configurar la contraseña de aplicación de Gmail. Aquí ves cada correo que el sistema enviaría.
        </div>
      ))}
      <Tabs defaultValue="notes">
        <TabsList><TabsTrigger value="notes" data-testid="inbox-tab-notifications">Correos de notificación ({notes.length})</TabsTrigger><TabsTrigger value="msgs" data-testid="inbox-tab-messages">Mensajes de contacto ({msgs.length})</TabsTrigger></TabsList>
        <TabsContent value="notes" className="mt-4">
          <div className="flex justify-end mb-3"><button onClick={readAll} className="text-sm brand-text font-medium flex items-center gap-1.5"><CheckCheck className="w-4 h-4" />Marcar todo como leído</button></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
            {notes.length === 0 && <p className="p-8 text-center text-gray-500 text-sm">Sin notificaciones</p>}
            {notes.map((n) => {
              const [l, Icon, c] = KIND[n.kind] || [n.kind, Mail, "bg-gray-100"];
              return (
                <details key={n.id} className="group p-4" data-testid="notification-item">
                  <summary className="flex items-center gap-3 cursor-pointer list-none">
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${c}`}><Icon className="w-4 h-4" /></span>
                    <div className="flex-1 min-w-0"><div className={`text-sm truncate ${n.read ? "text-gray-700" : "font-semibold text-gray-900"}`}>{n.subject}</div><div className="text-xs text-gray-500">Para: {n.to} · {l} · {new Date(n.created_at).toLocaleString("es-DO")}</div></div>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${{ enviado: "bg-emerald-100 text-emerald-700", error: "bg-red-100 text-red-700", enviando: "bg-sky-100 text-sky-700" }[n.status] || "bg-gray-100 text-gray-600"}`}>{n.status}</span>
                    {n.attachments?.length > 0 && <span className="text-[11px] text-gray-500 hidden sm:flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{n.attachments.length}</span>}
                    {!n.read && <span className="w-2 h-2 rounded-full accent-bg" />}
                  </summary>
                  {n.error && <p className="mt-3 ml-12 text-xs text-red-600">Error: {n.error}</p>}
                  <pre className="mt-3 ml-12 whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 rounded-lg p-3 font-sans">{n.body}</pre>
                </details>
              );
            })}
          </div>
        </TabsContent>
        <TabsContent value="msgs" className="mt-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 divide-y divide-gray-100">
            {msgs.length === 0 && <p className="p-8 text-center text-gray-500 text-sm">Sin mensajes</p>}
            {msgs.map((m) => (
              <div key={m.id} className="p-4" data-testid="message-item">
                <div className="flex items-center gap-3">
                  <button onClick={() => readMsg(m)} className="flex-1 text-left min-w-0">
                    <div className={`text-sm ${m.read ? "text-gray-700" : "font-semibold text-gray-900"}`}>{m.name} <span className="font-normal text-gray-500">&lt;{m.email}&gt;</span></div>
                    <div className="text-xs text-gray-500 truncate">{m.message}</div>
                  </button>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{new Date(m.created_at).toLocaleDateString("es-DO")}</span>
                  <a href={`mailto:${m.email}`} className="p-2 rounded hover:bg-gray-100" aria-label="Responder"><Mail className="w-4 h-4" /></a>
                  <button onClick={() => delMsg(m)} className="p-2 rounded hover:bg-red-50 text-red-500" aria-label="Eliminar"><Trash2 className="w-4 h-4" /></button>
                </div>
                {open === m.id && <p className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{m.message}</p>}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
